import { useEffect, useState, useMemo, useCallback } from "react";
import { useLoaderData } from "react-router-dom";
import toast from "react-hot-toast";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../store/organization";
import { useEventStore } from "../../store/event";
import { GET_EVENTS, GET_TIMECLOCK_REPORT } from "./queries";

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;

interface TimeclockEntry {
  id: number;
  worker_id: number;
  event_id: number;
  clock_in: string;
  clock_out: string | null;
  worker: {
    id: number;
    name: string;
    hourly_rate: number | null;
    vendor_id: number;
    vendor: { id: number; name: string };
    worker_role_assignments: Array<{ worker_role: { name: string } }>;
  };
}

interface Event {
  id: number;
  name: string;
}

interface WorkerSummary {
  workerId: number;
  workerName: string;
  roles: string;
  hourlyRate: number | null;
  totalHours: number;
  laborCost: number;
  entries: EntryRow[];
}

interface VendorSummary {
  vendorId: number;
  vendorName: string;
  totalHours: number;
  laborCost: number;
  workerCount: number;
  workers: WorkerSummary[];
}

interface EntryRow {
  id: number;
  clockIn: string;
  clockOut: string | null;
  hours: number;
  cost: number;
}

// ===== Helpers =====
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const calcHours = (clockIn: string, clockOut: string | null): number => {
  if (!clockOut) return 0;
  return (new Date(clockOut).getTime() - new Date(clockIn).getTime()) / 3600000;
};

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const hrs = (h: number) => `${h.toFixed(1)}h`;

// ===== Stat Card =====
const StatCard = ({ label, value, color = "text-gray-900" }: { label: string; value: string; color?: string }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    <span className={`text-lg font-bold ${color} tabular-nums`}>{value}</span>
  </div>
);

// ===== Loader =====
export const timeclockReportLoader = async () => {
  const organizationId = getOrgState().organizationId;
  const where = organizationId ? { organization_id: { _eq: organizationId } } : {};

  const { data } = await client.query({ query: GET_EVENTS, variables: { where } });
  return { events: data.events as Event[] };
};

// ===== Page =====
export const TimeclockReport = () => {
  const loaderData = useLoaderData() as { events: Event[] };
  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);
  const selectedEventId = headerEventId ? Number(headerEventId) : null;

  const [events, setEvents] = useState<Event[]>(loaderData.events);
  const [entries, setEntries] = useState<TimeclockEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState<Set<number>>(new Set());
  const [expandedWorkers, setExpandedWorkers] = useState<Set<string>>(new Set());

  // Refetch the events list (used for CSV export filename) when org changes.
  useEffect(() => {
    if (!headerOrgId) {
      setEvents([]);
      return;
    }
    client.query({
      query: GET_EVENTS,
      variables: { where: { organization_id: { _eq: Number(headerOrgId) } } },
      fetchPolicy: "network-only",
    })
      .then(({ data }) => setEvents(data.events))
      .catch((err: any) => toast.error("Failed to load events: " + (err?.message ?? "")));
  }, [headerOrgId]);

  const fetchReport = useCallback(async (eventId: number) => {
    setIsLoading(true);
    try {
      const { data } = await client.query({
        query: GET_TIMECLOCK_REPORT,
        variables: { eventId },
        fetchPolicy: "network-only",
      });
      setEntries(data.timeclock_entries);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchReport(selectedEventId);
      setExpandedVendors(new Set());
      setExpandedWorkers(new Set());
    } else {
      setEntries([]);
    }
  }, [selectedEventId, fetchReport]);

  // ===== Build vendor → worker → entries hierarchy =====
  const vendorSummaries: VendorSummary[] = useMemo(() => {
    const vendorMap = new Map<number, VendorSummary>();

    for (const entry of entries) {
      const v = entry.worker.vendor;
      const w = entry.worker;
      const hours = calcHours(entry.clock_in, entry.clock_out);
      const cost = w.hourly_rate ? hours * w.hourly_rate : 0;

      if (!vendorMap.has(v.id)) {
        vendorMap.set(v.id, {
          vendorId: v.id,
          vendorName: v.name,
          totalHours: 0,
          laborCost: 0,
          workerCount: 0,
          workers: [],
        });
      }
      const vendor = vendorMap.get(v.id)!;

      let worker = vendor.workers.find((ws) => ws.workerId === w.id);
      if (!worker) {
        worker = {
          workerId: w.id,
          workerName: w.name,
          roles: w.worker_role_assignments.map((a) => a.worker_role.name).join(", "),
          hourlyRate: w.hourly_rate,
          totalHours: 0,
          laborCost: 0,
          entries: [],
        };
        vendor.workers.push(worker);
      }

      worker.entries.push({
        id: entry.id,
        clockIn: entry.clock_in,
        clockOut: entry.clock_out,
        hours,
        cost,
      });

      worker.totalHours += hours;
      worker.laborCost += cost;
      vendor.totalHours += hours;
      vendor.laborCost += cost;
    }

    for (const vendor of vendorMap.values()) {
      vendor.workerCount = vendor.workers.length;
      vendor.workers.sort((a, b) => a.workerName.localeCompare(b.workerName));
    }

    return Array.from(vendorMap.values()).sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  }, [entries]);

  const totals = useMemo(
    () => ({
      hours: vendorSummaries.reduce((s, v) => s + v.totalHours, 0),
      cost: vendorSummaries.reduce((s, v) => s + v.laborCost, 0),
      entries: entries.length,
      vendors: vendorSummaries.length,
      workers: vendorSummaries.reduce((s, v) => s + v.workerCount, 0),
    }),
    [vendorSummaries, entries]
  );

  const toggleVendor = (id: number) => {
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleWorker = (key: string) => {
    setExpandedWorkers((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ===== CSV Export =====
  const exportCSV = () => {
    const rows: string[][] = [
      ["Vendor", "Worker", "Roles", "Hourly Rate", "Clock In", "Clock Out", "Hours", "Labor Cost"],
    ];

    for (const vendor of vendorSummaries) {
      for (const worker of vendor.workers) {
        for (const entry of worker.entries) {
          rows.push([
            vendor.vendorName,
            worker.workerName,
            worker.roles,
            worker.hourlyRate != null ? worker.hourlyRate.toFixed(2) : "",
            new Date(entry.clockIn).toLocaleString(),
            entry.clockOut ? new Date(entry.clockOut).toLocaleString() : "Active",
            entry.hours.toFixed(2),
            entry.cost.toFixed(2),
          ]);
        }
      }
    }

    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const eventName = events.find((e) => e.id === selectedEventId)?.name ?? "timeclock";
    a.download = `timeclock_report_${eventName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900">Timeclock report</h1>
          {selectedEventId && entries.length > 0 && (
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Export CSV
            </button>
          )}
        </div>

        {selectedEventId && !isLoading && entries.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total hours" value={hrs(totals.hours)} />
            <StatCard label="Labor cost" value={`$${totals.cost.toFixed(2)}`} />
            <StatCard label="Entries" value={String(totals.entries)} />
            <StatCard label="Vendors" value={String(totals.vendors)} />
            <StatCard label="Workers" value={String(totals.workers)} />
          </div>
        )}
      </div>

      {isLoading && <div className="text-center py-12 text-gray-400">Loading report...</div>}

      {/* Vendor breakdown */}
      {selectedEventId && !isLoading && vendorSummaries.length > 0 && (
        <div className="space-y-4">
          {vendorSummaries.map((vendor) => {
            const isVendorExpanded = expandedVendors.has(vendor.vendorId);

            return (
              <div key={vendor.vendorId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Vendor header */}
                <button
                  onClick={() => toggleVendor(vendor.vendorId)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isVendorExpanded ? "rotate-90" : ""}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{vendor.vendorName}</div>
                      <div className="text-xs text-gray-400">
                        {vendor.workerCount} worker{vendor.workerCount !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <span className="text-gray-500 tabular-nums">{hrs(vendor.totalHours)}</span>
                    <span className="font-bold text-gray-900 tabular-nums">${vendor.laborCost.toFixed(2)}</span>
                  </div>
                </button>

                {/* Workers */}
                {isVendorExpanded && (
                  <div className="border-t border-gray-100">
                    {vendor.workers.map((worker) => {
                      const workerKey = `${vendor.vendorId}::${worker.workerId}`;
                      const isWorkerExpanded = expandedWorkers.has(workerKey);

                      return (
                        <div key={worker.workerId}>
                          <button
                            onClick={() => toggleWorker(workerKey)}
                            className="w-full flex items-center justify-between px-5 py-3 pl-10 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <svg
                                className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isWorkerExpanded ? "rotate-90" : ""}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <div className="text-left">
                                <span className="text-sm font-medium text-gray-700">{worker.workerName}</span>
                                {worker.roles && (
                                  <span className="text-xs text-gray-400 ml-2">{worker.roles}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-5 text-xs flex-shrink-0">
                              <span className="text-gray-500 tabular-nums">{hrs(worker.totalHours)}</span>
                              {worker.hourlyRate != null && (
                                <span className="text-gray-400 tabular-nums">${worker.hourlyRate.toFixed(2)}/hr</span>
                              )}
                              <span className="font-semibold text-gray-700 tabular-nums">${worker.laborCost.toFixed(2)}</span>
                              <span className="text-gray-400">{worker.entries.length} entries</span>
                            </div>
                          </button>

                          {/* Entries */}
                          {isWorkerExpanded && (
                            <div className="bg-gray-50 px-5 pl-16 py-2">
                              <div className="grid grid-cols-12 gap-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200 mb-1">
                                <div className="col-span-3">Date</div>
                                <div className="col-span-3">Clock in</div>
                                <div className="col-span-3">Clock out</div>
                                <div className="col-span-1">Hours</div>
                                <div className="col-span-2 text-right">Cost</div>
                              </div>
                              {worker.entries.map((entry) => (
                                <div
                                  key={entry.id}
                                  className="grid grid-cols-12 gap-2 py-1.5 text-sm text-gray-600 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="col-span-3">{formatDate(entry.clockIn)}</div>
                                  <div className="col-span-3">{formatTime(entry.clockIn)}</div>
                                  <div className="col-span-3">
                                    {entry.clockOut ? (
                                      formatTime(entry.clockOut)
                                    ) : (
                                      <span className="text-red-600 font-semibold">Active</span>
                                    )}
                                  </div>
                                  <div className="col-span-1 tabular-nums">{entry.hours.toFixed(1)}</div>
                                  <div className="col-span-2 text-right tabular-nums">
                                    {entry.cost > 0 ? `$${entry.cost.toFixed(2)}` : "—"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedEventId && !isLoading && entries.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center py-20 text-gray-400 text-sm">
          No timeclock data for this event
        </div>
      )}

      {!selectedEventId && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center py-20 text-gray-400 text-sm">
          Select an event in the header to view the timeclock report
        </div>
      )}
    </div>
  );
};
